class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input.field__input');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.predictiveSearchResultsArticle = this.querySelector('[data-predictive-search-article]');
    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      $('form.search-modal__form .predictive-search-content').css("display", "none");
    }
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    this.setLiveRegionLoadingState();

    this._searchGeneration = (this._searchGeneration || 0) + 1;
    const myGeneration = this._searchGeneration;

    const productPromise = this.fetchProductSuggestions(searchTerm)
      .then((markup) => {
        if (this.hasNoResults(markup) && searchTerm.trim().split(/\s+/).length > 1) {
          return this.fetchWordFallback(searchTerm, markup);
        }
        return markup;
      })
      .then(async (markup) => {
        if (!this.hasNoResults(markup)) return { markup, correctedFrom: null, collection: null };
        const vocabulary = await this.loadVocabulary();
        if (!vocabulary.length) return { markup, correctedFrom: null, collection: null };

        const words = searchTerm.trim().toLowerCase().split(/\s+/).filter(Boolean);
        const hasUnknown = words.some((w) => vocabulary.indexOf(w) === -1);
        if (!hasUnknown) {
          const collection = await this.fetchCollectionMatch(searchTerm);
          return { markup, correctedFrom: null, collection };
        }

        const { corrected, query } = this.correctQuery(searchTerm, vocabulary);
        if (!corrected || query === searchTerm.trim().toLowerCase()) return { markup, correctedFrom: null, collection: null };
        const correctedMarkup = await this.fetchProductSuggestions(query);
        if (this.hasNoResults(correctedMarkup)) return { markup, correctedFrom: null, collection: null };
        return { markup: correctedMarkup, correctedFrom: query, collection: null };
      })
      .then(({ markup, correctedFrom, collection }) => {
        if (myGeneration !== this._searchGeneration) return;
        let finalMarkup = markup;
        if (collection) {
          finalMarkup =
            `<p class="predictive-search__collection-match">Browse <a href="${collection.url}"><strong>${collection.title}</strong></a></p>` +
            markup;
        } else if (correctedFrom) {
          finalMarkup =
            `<p class="predictive-search__spell-correction">Showing results for "<strong>${correctedFrom}</strong>"</p>` +
            markup;
        }
        this.renderSearchResults(finalMarkup, (correctedFrom || searchTerm).replace(/\s+/g, '-').toLowerCase());
      })
      .catch((error) => {
        this.close();
        throw error;
      });

    const articleUrl = `${routes.predictive_search_url}` +
      `?q=${encodeURIComponent(searchTerm)}` +
      `&resources[type]=article` +
      `&section_id=predictive-search`;

    const articlePromise = fetch(articleUrl)
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const blogEl = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#predictive-search-blog');
        const resultsMarkup = blogEl ? blogEl.innerHTML : '';
        this.renderSearchResultsArticle(resultsMarkup, searchTerm.replace(/\s+/g, '-').toLowerCase());
      })
      .catch((error) => {
        this.close();
        throw error;
      });

    productPromise.finally(() => {
      this.setLiveRegionResults();
    });
  }

  buildProductUrl(q) {
    return `${routes.predictive_search_url}` +
      `?q=${encodeURIComponent(q)}` +
      `&resources[type]=product,collection` +
      `&resources[options][fields]=title,variants.sku,product_type,tag` +
      `&section_id=predictive-search`;
  }

  fetchProductSuggestions(q) {
    return fetch(this.buildProductUrl(q))
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#predictive-search-product')
        .innerHTML);
  }

  hasNoResults(markup) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    return doc.querySelectorAll('.ss__result').length === 0;
  }

  loadAllCollections() {
    if (this._collectionsPromise) return this._collectionsPromise;
    this._collectionsPromise = fetch('/collections.json?limit=250')
      .then((res) => (res.ok ? res.json() : { collections: [] }))
      .then((data) => data.collections || [])
      .catch(() => []);
    return this._collectionsPromise;
  }

  fetchCollectionMatch(q) {
    const query = q.trim().toLowerCase();
    return this.loadAllCollections().then((collections) => {
      const match = collections.find((c) => c.title.toLowerCase().includes(query));
      return match ? { title: match.title, url: `/collections/${match.handle}` } : null;
    });
  }

  extractResultArticles(markup) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    return Array.from(doc.querySelectorAll('.ss__result'));
  }

  fetchWordFallback(searchTerm, emptyMarkup) {
    const words = searchTerm.trim().split(/\s+/).filter((w) => w.length > 1);
    if (!words.length) return Promise.resolve(emptyMarkup);

    return Promise.all(words.map((w) => this.fetchProductSuggestions(w)))
      .then((markups) => {
        const seen = new Set();
        const merged = [];
        markups.forEach((markup) => {
          this.extractResultArticles(markup).forEach((article) => {
            const link = article.querySelector('a[href]');
            const key = link ? link.getAttribute('href') : article.outerHTML;
            if (seen.has(key)) return;
            seen.add(key);
            merged.push(article.outerHTML);
          });
        });
        if (!merged.length) return emptyMarkup;

        const wrapperDoc = new DOMParser().parseFromString(emptyMarkup, 'text/html');
        const heading = wrapperDoc.querySelector('.predictive-search__heading');
        const headingHtml = heading ? heading.outerHTML : '';
        const infoBlock = wrapperDoc.querySelector('.ss__autocomplete__content__info');
        const infoHtml = infoBlock ? infoBlock.outerHTML : '';
        return (
          '<div class="predictive-search__wrapper predictive-search__wrapper--products" style="width:237px; overflow:auto;">' +
          headingHtml +
          merged.slice(0, 4).join('') +
          '</div>' +
          infoHtml
        );
      });
  }

  levenshtein(a, b) {
    const m = a.length, n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;
    const dp = [];
    for (let i = 0; i <= m; i++) dp.push(new Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + cost
        );
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          dp[i][j] = Math.min(dp[i][j], dp[i - 2][j - 2] + 1);
        }
      }
    }
    return dp[m][n];
  }

  prefixMatch(word, vocabulary) {
    const exact = vocabulary.filter((v) => v.length > word.length && v.indexOf(word) === 0);
    if (exact.length) {
      exact.sort((a, b) => a.length - b.length);
      return { word: exact[0], distance: 0 };
    }
    const maxDist = word.length <= 4 ? 1 : 2;
    let best = null, bestDist = Infinity;
    for (const candidate of vocabulary) {
      if (candidate.length < word.length + 2) continue;
      const dist = this.levenshtein(word, candidate.slice(0, word.length));
      if (dist <= maxDist && dist < bestDist) {
        bestDist = dist;
        best = candidate;
      }
    }
    return best ? { word: best, distance: bestDist } : null;
  }

  loadVocabulary() {
    if (this._vocabPromise) return this._vocabPromise;
    this._vocabPromise = fetch('https://adamand.com/cdn/shop/t/119/assets/search-vocabulary.json?v=123')
      .then((res) => (res.ok ? res.json() : []))
      .catch(() => []);
    return this._vocabPromise;
  }

  stripAccents(s) {
    return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  correctQuery(query, vocabulary) {
    const words = this.stripAccents(query.trim().toLowerCase()).split(/\s+/).filter(Boolean);
    let anyCorrected = false;
    const corrected = words.map((word) => {
      if (vocabulary.indexOf(word) !== -1) return word;

      const prefixHit = this.prefixMatch(word, vocabulary);

      const maxDist = word.length <= 4 ? 1 : word.length <= 8 ? 2 : 3;
      let fullBest = null;
      let fullBestDist = Infinity;
      for (const candidate of vocabulary) {
        if (Math.abs(candidate.length - word.length) > maxDist) continue;
        const dist = this.levenshtein(word, candidate);
        if (dist < fullBestDist) {
          fullBestDist = dist;
          fullBest = candidate;
        }
        if (fullBestDist === 1) break;
      }

      const fullHit = fullBest && fullBestDist <= maxDist ? { word: fullBest, distance: fullBestDist } : null;

      if (prefixHit && (!fullHit || prefixHit.distance < fullHit.distance)) {
        anyCorrected = true;
        return prefixHit.word;
      }
      if (fullHit) {
        anyCorrected = true;
        return fullHit.word;
      }
      return word;
    });
    return { corrected: anyCorrected, query: corrected.join(' ') };
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, queryKey) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    const url = `/search?q=${queryKey}&type=product`;
    $('[data-predictive-search] [data-view-all-product]').attr('href', url);
    this.open();
  }

  renderSearchResultsArticle(resultsMarkup, queryKey) {
    if (!this.predictiveSearchResultsArticle) return;
    this.predictiveSearchResultsArticle.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    const url = `/search?q=${queryKey}&type=article`;
    $('[data-predictive-search-article] [data-view-all-article]').attr('href', url);
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    $('form.search-modal__form .predictive-search-content').removeAttr('style');
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
  }
}

customElements.define('predictive-search', PredictiveSearch);